import { FindingSpecDto, FlowSpecDto, SaasComponentKind } from '@ampsec/platform-client';
import AmplifierLogo from 'Assets/icons/AmpSec.svg';
import { startCase } from 'lodash';
import { isPendingEngagementsFilterByFindingsEnabled } from 'Core-utils/featureFlags/pendingEngagementsFilterByFindings';
import { parseTagBucketsFromTriggerFilter } from './flowSpecTriggerTags.util';

export type FlowSpecTriggerItem = NonNullable<FlowSpecDto['triggerFilter']>['trigger'][number];

export type ConnectorProviderLookup = {
  cid?: string;
  providerIcon?: string;
};

export type TargetFindingDisplayItem = {
  key: string;
  label: string;
  iconSrc?: string;
  showMicrotrainingAccessory: boolean;
};

function hasTagFilterBuckets(triggerFilter: FlowSpecDto['triggerFilter'] | undefined): boolean {
  const buckets = parseTagBucketsFromTriggerFilter(triggerFilter);
  return buckets.any.length > 0 || buckets.all.length > 0 || buckets.not.length > 0;
}

export function getFlowSpecTriggersFromFlowSpec(flowSpec: FlowSpecDto | undefined): FlowSpecTriggerItem[] {
  const triggers = flowSpec?.triggerFilter?.trigger ?? [];
  const findingTriggers = triggers.filter((trigger): trigger is FlowSpecTriggerItem =>
    Boolean(trigger?.id || trigger?.eventType || trigger?.displayValue),
  );

  if (findingTriggers.length > 0) {
    return findingTriggers;
  }

  if (
    isPendingEngagementsFilterByFindingsEnabled() &&
    hasTagFilterBuckets(flowSpec?.triggerFilter)
  ) {
    return [{ displayValue: 'Tag filters' } as FlowSpecTriggerItem];
  }

  return [];
}

function resolveTriggerLabel(
  trigger: FlowSpecTriggerItem,
  formatDisplayLabel: (value: string | null | undefined) => string,
): string {
  if (trigger.displayValue?.trim()) {
    return startCase(trigger.displayValue);
  }
  if (trigger.eventType) {
    return formatDisplayLabel(trigger.eventType.toLowerCase());
  }
  return '-';
}

export function resolveTargetFindingDisplayItems(
  flowSpec: FlowSpecDto | undefined,
  options: {
    connectorProviderMap: ConnectorProviderLookup[];
    findingSpecById: Record<string, FindingSpecDto>;
    formatDisplayLabel: (value: string | null | undefined) => string;
  },
): TargetFindingDisplayItem[] {
  const { connectorProviderMap, findingSpecById, formatDisplayLabel } = options;

  return getFlowSpecTriggersFromFlowSpec(flowSpec).map((trigger, index) => {
    const findingSpec = trigger.id ? findingSpecById[trigger.id] : undefined;
    const isMicrotrainingFinding = findingSpec?.insights?.rule?.kind === SaasComponentKind.MICROTRAINING;
    const provider = connectorProviderMap.find((entry) => entry.cid === trigger.cid);
    const iconSrc = !trigger.cid?.trim() ? AmplifierLogo : provider?.providerIcon;

    return {
      key: trigger.id ?? `${trigger.eventType ?? 'tag-filters'}-${index}`,
      label: resolveTriggerLabel(trigger, formatDisplayLabel),
      iconSrc,
      showMicrotrainingAccessory: Boolean(trigger.cid?.trim()) && isMicrotrainingFinding,
    };
  });
}

export function formatTargetFindingsForSearch(
  flowSpec: FlowSpecDto | undefined,
  options: {
    connectorProviderMap: ConnectorProviderLookup[];
    findingSpecById: Record<string, FindingSpecDto>;
    formatDisplayLabel: (value: string | null | undefined) => string;
  },
): string {
  const items = resolveTargetFindingDisplayItems(flowSpec, options);
  if (items.length === 0) {
    return '';
  }
  return items.map((item) => item.label).join(' ');
}

export function getTargetFindingCount(flowSpec: FlowSpecDto | undefined): number {
  return getFlowSpecTriggersFromFlowSpec(flowSpec).length;
}

export function getEngagementFlowRowHeightForFindingCount(
  findingCount: number,
  baseRowHeight: number,
  findingLineHeight: number,
): number {
  if (findingCount <= 1) {
    return baseRowHeight;
  }

  return baseRowHeight + (findingCount - 1) * findingLineHeight;
}
